import React, { useState, useEffect } from 'react';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';

const newBlockLocations = [
    "Hand Wash Area", "Plant Entry", "Near Washing Area", "Near Emergency Gate",
    "Near Packing Area", "Invert Syrup Room", "Broken Room", "Premixing Room",
    "Mixing Entrance", "Washing Area", "Infront of Terrace Gate",
    "New Plant Worker Entry 1", "New Plant Worker Entry 2", "Cream Room",
    "Line 7 Packing", "RM Unloading Bay 1", "RM Unloading Bay 2",
    "Line 6 Packing", "RM Store 1st Floor"
];

const oldBlockLocations = [
    "RM Unloading Bay", "Biscuit Grinding Room", "RM Storage", "Outside Cold Room",
    "Maintenance Change Room", "Outside Maintenance Change Room", "Oven Area",
    "Outside QA Lab", "Staff Hand Wash", "Outside Staff Canteen", "Staff Canteen",
    "Near Worker Entry", "Worker Entry Hand Wash Area", "Outside HR Office",
    "Near PM Warehouse Door", "Near PM Warehouse", "Near FG Warehouse Door",
    "Outside DH Room", "Near Emergency Exit 1", "Near Emergency Exit 2",
    "Near Emergency Exit 3", "Near Emergency Exit 4", "Near Emergency Exit 5",
    "Wash Area", "Mixing Section HAAS Line", "Cooling Conveyor HAAS Line",
    "Packing Section HAAS Line", "Outside Flavour Room", "New Cold Room Gallery 1",
    "New Cold Room Gallery 2", "PM Warehouse", "FG Warehouse", "Worker Canteen",
    "Mondelez Packing", "Line 8 Packing", "Line 8 Mixing", "FG Main Warehouse"
];

interface Observation {
    id: string;
    type: string;
    count: number;
}

interface LocationData {
    name: string;
    status: 'Okay' | 'Not Okay' | '';
    observations: Observation[];
}

const generateId = () => Math.random().toString(36).substr(2, 9);

const PCIChecklist: React.FC = () => {
    const { state, dispatch } = useFoodSafetyContext();
    const [area, setArea] = useState(state.sessionData?.area || 'Old Block');
    const [sessionStarted, setSessionStarted] = useState(true);
    const [locations, setLocations] = useState<LocationData[]>(() => {
        const selectedArea = state.sessionData?.area || 'Old Block';
        const locationNames = selectedArea === 'New Block' ? newBlockLocations : oldBlockLocations;
        return locationNames.map(name => ({
            name,
            status: '',
            observations: []
        }));
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize locations when session starts
    useEffect(() => {
        if (sessionStarted) {
            const locationNames = area === 'New Block' ? newBlockLocations : oldBlockLocations;
            setLocations(locationNames.map(name => ({
                name,
                status: '',
                observations: []
            })));
        }
    }, [sessionStarted, area]);

    const hasLoadedRef = React.useRef<string | null>(null);

    // Resume logic: pre-fill from paused data
    useEffect(() => {
        const loadResumedData = async () => {
            if (state.isPaused && state.currentTourId && hasLoadedRef.current !== state.currentTourId) {
                hasLoadedRef.current = state.currentTourId;
                try {
                    const items = await foodSafetyService.getChecklistItems(state.currentTourId);
                    if (items && items.length > 0) {
                        // Determine the area from first item
                        const pausedArea = items[0].cr953_food_safety_area || 'Old Block';
                        setArea(pausedArea);

                        const locationNames = pausedArea === 'New Block' ? newBlockLocations : oldBlockLocations;
                        const rebuilt: LocationData[] = locationNames.map(name => {
                            const matches = items.filter((d: any) => d.cr953_food_safety_location === name);
                            if (matches.length === 0) return { name, status: '', observations: [] };

                            const isOkay = matches.every((m: any) => m.cr953_food_safety_status === 'Okay');
                            if (isOkay) {
                                return { name, status: 'Okay', observations: [] };
                            } else {
                                return {
                                    name,
                                    status: 'Not Okay',
                                    observations: matches
                                        .filter((m: any) => m.cr953_food_safety_status === 'Not Okay')
                                        .map((m: any) => ({
                                            id: generateId(),
                                            type: m.cr953_food_safety_observationtype || 'Others',
                                            count: parseInt(m.cr953_food_safety_defectcount, 10) || 1
                                        }))
                                };
                            }
                        });
                        setLocations(rebuilt);
                        setSessionStarted(true);
                    }
                } catch (error) {
                    console.error("Failed to load paused PCI items", error);
                }
            }
        };
        loadResumedData();
    }, [state.isPaused, state.currentTourId]);

    const handleStatusChange = (index: number, status: 'Okay' | 'Not Okay') => {
        const newLocs = [...locations];
        newLocs[index].status = status;
        if (status === 'Okay') {
            newLocs[index].observations = [];
        } else if (status === 'Not Okay' && newLocs[index].observations.length === 0) {
            newLocs[index].observations = [{ id: generateId(), type: 'Others', count: 1 }];
        }
        setLocations(newLocs);
    };

    const addObservation = (locIndex: number) => {
        const newLocs = [...locations];
        newLocs[locIndex].observations.push({ id: generateId(), type: 'Others', count: 1 });
        setLocations(newLocs);
    };

    const removeObservation = (locIndex: number, obsId: string) => {
        const newLocs = [...locations];
        newLocs[locIndex].observations = newLocs[locIndex].observations.filter(o => o.id !== obsId);
        if (newLocs[locIndex].observations.length === 0) {
            newLocs[locIndex].status = '';
        }
        setLocations(newLocs);
    };

    const updateObservation = (locIndex: number, obsId: string, field: 'type' | 'count', value: any) => {
        const newLocs = [...locations];
        const obs = newLocs[locIndex].observations.find(o => o.id === obsId);
        if (obs) {
            if (field === 'type') obs.type = value;
            if (field === 'count') obs.count = parseInt(value) || 0;
        }
        setLocations(newLocs);
    };

    // Progress Tracker
    const totalAnswered = locations.filter(l => l.status !== '').length;
    const hasFails = locations.some(l => l.status === 'Not Okay');
    const progressPercent = locations.length > 0 ? Math.round((totalAnswered / locations.length) * 100) : 0;

    const saveRows = async () => {
        if (!state.currentTourId || !state.sessionData) return;
        await foodSafetyService.cleanChecklistItems(state.currentTourId);

        for (const loc of locations) {
            if (!loc.status) continue; // Skip unanswered on pause

            if (loc.status === 'Okay') {
                await foodSafetyService.saveChecklistRow({
                    cr953_food_safety_title: `PCI - ${state.sessionData.site} - ${loc.name}`,
                    "cr953_food_safety_tourid@odata.bind": `/cr3ea_prod_rajpura_quality_tours(${state.currentTourId})`,
                    cr953_food_safety_checklisttype: state.selectedChecklistType,
                    cr953_food_safety_manufacturingsite: state.sessionData.site,
                    cr953_food_safety_line: state.sessionData.line,
                    cr953_food_safety_qaexecutive: state.sessionData.qaExecutive,
                    cr953_food_safety_productionincharge: state.sessionData.productionIncharge,
                    cr953_food_safety_areaincharge: state.sessionData.areaIncharge,
                    cr953_food_safety_area: area,
                    cr953_food_safety_location: loc.name,
                    cr953_food_safety_cycle: state.sessionData.cycle,
                    cr953_food_safety_status: loc.status,
                    cr953_food_safety_date: state.sessionData.tourStartDate,
                });
            } else {
                for (const obs of loc.observations) {
                    await foodSafetyService.saveChecklistRow({
                        cr953_food_safety_title: `PCI - ${state.sessionData.site} - ${loc.name} - ${obs.type}`,
                        "cr953_food_safety_tourid@odata.bind": `/cr3ea_prod_rajpura_quality_tours(${state.currentTourId})`,
                        cr953_food_safety_checklisttype: state.selectedChecklistType,
                        cr953_food_safety_manufacturingsite: state.sessionData.site,
                        cr953_food_safety_line: state.sessionData.line,
                        cr953_food_safety_qaexecutive: state.sessionData.qaExecutive,
                        cr953_food_safety_productionincharge: state.sessionData.productionIncharge,
                        cr953_food_safety_areaincharge: state.sessionData.areaIncharge,
                        cr953_food_safety_area: area,
                        cr953_food_safety_location: loc.name,
                        cr953_food_safety_cycle: state.sessionData.cycle,
                        cr953_food_safety_status: loc.status,
                        cr953_food_safety_observationtype: obs.type,
                        cr953_food_safety_defectcount: obs.count.toString(),
                        cr953_food_safety_date: state.sessionData.tourStartDate,
                    });
                }
            }
        }
    };

    const handlePause = async () => {
        if (!state.currentTourId) return;
        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            await saveRows();
            await foodSafetyService.saveSession({
                cr3ea_prod_rajpura_quality_tourid: state.currentTourId,
                cr3ea_status: "InProgress-paused",
                cr3ea_food_safety_area: area
            });
            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.WELCOME });
        } catch (error) {
            console.error("Error pausing PCI Checklist:", error);
            alert("Failed to pause checklist.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const handleSubmit = async () => {
        const allAnswered = locations.every(loc => loc.status !== '');
        if (!allAnswered) {
            alert("Please provide a status for all locations.");
            return;
        }

        const missingObsDetails = locations.some(loc =>
            loc.status === 'Not Okay' &&
            loc.observations.some(obs => obs.count <= 0 || !obs.type)
        );
        if (missingObsDetails) {
            alert("Please ensure all observations have a valid type and count greater than 0.");
            return;
        }

        if (!state.currentTourId || !state.sessionData) {
            alert("Session missing. Please start again.");
            return;
        }

        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            await saveRows();

            await foodSafetyService.saveSession({
                cr3ea_prod_rajpura_quality_tourid: state.currentTourId,
                cr3ea_checklist_result: hasFails ? "Fail" : "Pass",
                cr3ea_status: "Submitted",
                cr3ea_tourcompletiondate: new Date().toISOString(),
                cr3ea_food_safety_area: area
            });

            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.SUMMARY });

        } catch (error: any) {
            console.error("Error submitting PCI Checklist:", error);
            alert("Failed to submit checklist. Please check your connection.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div>
            {/* Stepper Workflow Bar */}
            <div style={{ marginBottom: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e2e8f0', color: '#475569', padding: '4px 10px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        1. Request Info
                    </span>
                    <span style={{ color: '#94a3b8' }}>➔</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#80cbcf', color: '#ffffff', padding: '4px 12px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        2. PCI Checklist ({area})
                    </span>
                </div>
            </div>

            {/* Sticky Progress Banner */}
            <div className="card shadow-sm border-0 mb-4 sticky-top" style={{ top: '10px', zIndex: 1000 }}>
                <div className="card-body py-2 px-4 d-flex flex-wrap justify-content-between align-items-center bg-light rounded gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-secondary">
                            Progress: <span className="text-primary">{totalAnswered}/{locations.length}</span>
                        </span>
                        <div className="progress" style={{ width: '120px', height: '8px' }}>
                            <div className="progress-bar bg-primary" style={{ width: `${progressPercent}%` }}></div>
                        </div>
                        <span className={`badge ${hasFails ? 'bg-danger' : totalAnswered === locations.length ? 'bg-success' : 'bg-secondary'}`}>
                            {hasFails ? 'Non-Compliant' : totalAnswered === locations.length ? 'Compliant' : 'In Progress'}
                        </span>
                    </div>
                    <div>
                        <button className="btn btn-sm btn-outline-warning fw-bold me-2" onClick={handlePause} disabled={isSubmitting}>
                            <i className="bi bi-pause-circle me-1"></i>Pause Tour
                        </button>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 border-bottom-0">
                    <h5 className="card-title mb-0 fw-bold">PCI Checklist — {area}</h5>
                </div>
                <div className="card-body p-4">
                    {locations.map((loc, lIndex) => (
                        <div key={loc.name} className="card mb-4 shadow-sm">
                            <div className={`card-header d-flex justify-content-between align-items-center ${loc.status === 'Not Okay' ? 'bg-warning bg-opacity-25' : loc.status === 'Okay' ? 'bg-success bg-opacity-10' : 'bg-light'}`}>
                                <h6 className="mb-0 fw-bold">{lIndex + 1}. {loc.name}</h6>
                                <div className="d-flex gap-3">
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name={`pci-status-${lIndex}`}
                                            id={`pci-okay-${lIndex}`}
                                            checked={loc.status === 'Okay'}
                                            onChange={() => handleStatusChange(lIndex, 'Okay')}
                                        />
                                        <label className="form-check-label text-success fw-semibold" htmlFor={`pci-okay-${lIndex}`}>Okay</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name={`pci-status-${lIndex}`}
                                            id={`pci-notokay-${lIndex}`}
                                            checked={loc.status === 'Not Okay'}
                                            onChange={() => handleStatusChange(lIndex, 'Not Okay')}
                                        />
                                        <label className="form-check-label text-danger fw-semibold" htmlFor={`pci-notokay-${lIndex}`}>Not Okay</label>
                                    </div>
                                </div>
                            </div>

                            {loc.status === 'Not Okay' && (
                                <div className="card-body">
                                    <h6 className="text-danger mb-3"><i className="bi bi-exclamation-triangle-fill me-2"></i>Observations</h6>
                                    {loc.observations.map((obs) => (
                                        <div key={obs.id} className="row g-3 align-items-center mb-3 pb-3 border-bottom">
                                            <div className="col-md-5">
                                                <label className="form-label text-muted small mb-1">Observation Type</label>
                                                <select
                                                    className="form-select"
                                                    value={obs.type}
                                                    onChange={(e) => updateObservation(lIndex, obs.id, 'type', e.target.value)}
                                                >
                                                    <option value="Others">Others</option>
                                                    <option value="Lizard">Lizard</option>
                                                    <option value="Insects">Insects</option>
                                                    <option value="Rodents">Rodents</option>
                                                    <option value="Cockroach">Cockroach</option>
                                                    <option value="Store Insects">Store Insects</option>
                                                </select>
                                            </div>
                                            <div className="col-md-5">
                                                <label className="form-label text-muted small mb-1">Count</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="1"
                                                    value={obs.count}
                                                    onChange={(e) => updateObservation(lIndex, obs.id, 'count', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-2 mt-4 pt-2 text-end">
                                                <button
                                                    className="btn btn-outline-danger btn-sm w-100"
                                                    onClick={() => removeObservation(lIndex, obs.id)}
                                                >
                                                    <i className="bi bi-trash3"></i> Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => addObservation(lIndex)}>
                                        <i className="bi bi-plus-circle me-2"></i>Add another observation
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}

                    <div className="d-flex justify-content-end mt-5 pt-3 border-top">
                        <button
                            className="btn btn-success px-5 btn-lg"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Complete PCI Checklist'} <i className="bi bi-check2-all ms-2"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PCIChecklist;
